// client/src/components/pages/LexiStar/Reports/DailySummary.tsx
//
// /lexistar/reports/daily/:date — the daily management summary (ST-00188
// M4-R2; FR-RPT-001 for one SAST day, FR-RPT-005 daily briefing; LS-09 §5.15
// as a document). The FR-RPT-001 set for the day against the previous day, a
// seven-day trend per figure, and the top exceptions to look at. M4-R1's
// `daily_management_summary` schedule renders this page with ?print=1 at the
// tenant's briefing time.
//
// Data (M4-A): GET /metrics (catalogue, permission-filtered — only those keys
// are asked for), GET /kpis?scope_type=tenant&grain=day&from=D&to=D&compare=
// previous (values, previous day, change, provisional/final), the same for the
// seven days to D (trend), and GET /kpis/top-exceptions?date=D — through
// services/lexistarAnalytics, the dashboard's client. The page never computes a
// figure: values, provenance and state are the semantic layer's.

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UnifiedDatePicker from '../../../common/UnifiedDatePicker';
import { Line, LineChart, YAxis } from 'recharts';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaDesktop, FaPrint } from 'react-icons/fa';
import { formatMiningDateLong } from '../../../../utils/dateUtils';
import type {
  AggregateScopeType,
  KpisResponse,
  MetricDefinition,
  MetricValue,
  TopException,
} from '../../../../types/lexistar-analytics';
import { deltaOf, formatValue } from '../Dashboard/metricFormat';
import { getKpis, getMetrics, getTopExceptions } from '../../../../services/lexistarAnalytics';
import { errorText, isObj, pickList } from '../../../../services/lexistarReports';
import {
  ALL_DAILY_KEYS,
  DAILY_SECTIONS,
  HEADLINE_KEYS,
  addDays,
  betterOf,
  changeWords,
  isDay,
  todaySast,
  yesterdaySast,
} from './dailyLogic';
import {
  ExportButtons,
  PaperMessage,
  Prov,
  ProvenanceKey,
  ReportDoc,
  ReportFrame,
  ReportRenderContext,
  Section,
  toolbarButton,
  toolbarPrimary,
  usePrintMode,
  type LoadState,
} from './reportAtoms';
import { useAuth } from '../../../../types/commonComponents';

interface DailyData {
  catalogue: Map<string, MetricDefinition>;
  day: KpisResponse;
  trend: KpisResponse | null;
  exceptions: TopException[] | null;
  keys: string[];
}

/** A KPI body with its collections defaulted, so the page can read them unguarded. */
function kpisOf(res: unknown): KpisResponse {
  const body = (isObj(res) ? res : {}) as Partial<KpisResponse>;
  return {
    ...(body as KpisResponse),
    series: Array.isArray(body.series) ? body.series : [],
    totals: isObj(body.totals) ? body.totals : {},
    previous: isObj(body.previous) ? body.previous : null,
    change: isObj(body.change) ? body.change : null,
  };
}

async function loadDaily(date: string, scope: { scope_type: string; scope_id?: string }): Promise<DailyData> {
  let catalogue = new Map<string, MetricDefinition>();
  try {
    const c = await getMetrics();
    catalogue = new Map(pickList<MetricDefinition>(c, 'metrics').map((m) => [m.key, m]));
  } catch {
    /* no catalogue: ask for the whole set and let the API filter */
  }
  const keys = catalogue.size ? ALL_DAILY_KEYS.filter((k) => catalogue.has(k)) : ALL_DAILY_KEYS;
  const base = {
    scope_type: scope.scope_type as AggregateScopeType,
    scope_id: scope.scope_id,
    grain: 'day' as const,
    metrics: keys,
  };
  const [day, trend, exceptions] = await Promise.allSettled([
    getKpis({ ...base, from: date, to: date, compare: 'previous' }).then(kpisOf),
    getKpis({ ...base, from: addDays(date, -6), to: date }).then(kpisOf),
    getTopExceptions(date).then((res) => pickList<TopException>(res, 'items', 'exceptions')),
  ]);
  if (day.status === 'rejected') throw day.reason;
  return {
    catalogue,
    day: day.value,
    trend: trend.status === 'fulfilled' ? trend.value : null,
    exceptions: exceptions.status === 'fulfilled' ? exceptions.value : null,
    keys,
  };
}

const ACCENT = '#45b7aa';
const QUIET = '#767d87';

/** Seven-day trend: one series in the de-emphasis grey, the day itself in the accent. */
function Spark({ values, label }: { values: Array<number | null>; label: string }) {
  const points = values.map((v, i) => ({ i, v }));
  if (points.filter((p) => p.v !== null).length < 2) return <span className="lxr-muted">—</span>;
  const last = points.length - 1;
  return (
    <span role="img" aria-label={label} title={label} style={{ display: 'inline-block' }}>
      <LineChart width={84} height={22} data={points} margin={{ top: 3, right: 4, bottom: 3, left: 4 }} className="lxr-spark">
        <YAxis hide domain={['dataMin', 'dataMax']} />
        <Line
          type="linear"
          dataKey="v"
          stroke={QUIET}
          strokeWidth={1.5}
          isAnimationActive={false}
          connectNulls
          dot={(props: { cx?: number; cy?: number; index?: number }) =>
            props.index === last && props.cx != null && props.cy != null ? (
              <circle key="last" cx={props.cx} cy={props.cy} r={3} fill={ACCENT} stroke="#ffffff" strokeWidth={1.5} />
            ) : (
              <g key={`d${props.index}`} />
            )
          }
        />
      </LineChart>
    </span>
  );
}

/**
 * The report's day: the shared single-date picker — a field opening the day popup on a desktop
 * (ST-00294), a full-width button opening the day sheet on a phone (ST-00204).
 */
function DayField({ value, onChange }: { value: string; onChange: (day: string) => void }) {
  return (
    <UnifiedDatePicker
      value={new Date(`${value}T12:00:00`)}
      onChange={(d: Date | null) => {
        if (!d) return;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        onChange(`${y}-${m}-${dd}`);
      }}
      maxDate={new Date(`${todaySast()}T12:00:00`)}
      dateFormat="yyyy/MM/dd"
      className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm"
      aria-label="Day"
      autoComplete="off"
      title="Choose the day"
    />
  );
}

function DailyDocument({
  date,
  data,
  print,
  generatedAt,
  scopeLabel,
}: {
  date: string;
  data: DailyData;
  print: boolean;
  generatedAt: string | null;
  scopeLabel: string | null;
}) {
  const { day, trend, catalogue } = data;
  const values = day.totals;
  const previous = day.previous?.totals ?? {};
  const state = day.state ?? day.series[0]?.state ?? null;
  const nameOf = (k: string) => catalogue.get(k)?.name || k;
  const unitOf = (k: string, v?: MetricValue) => String(v?.unit ?? catalogue.get(k)?.unit ?? '');
  const trendOf = (k: string): Array<number | null> =>
    (trend?.series ?? []).map((p) => {
      const v = p.values[k]?.value;
      return v === null || v === undefined ? null : Number(v);
    });
  const available = new Set(Object.keys(values));
  const headline = HEADLINE_KEYS.filter((k) => available.has(k));

  const drillOf = (v: MetricValue | undefined) => v?.drill?.route || null;

  return (
    <ReportDoc
      print={print}
      label={`Daily management summary ${date}`}
      reportName="Daily management summary"
      title={formatMiningDateLong(`${date}T12:00:00+02:00`) || date}
      subtitle={
        <>
          The day against {formatMiningDateLong(`${addDays(date, -1)}T12:00:00+02:00`)} · {scopeLabel || 'all visible trips'}
        </>
      }
      chips={
        state ? (
          <span className={`lxr-chip ${state === 'final' ? 'lxr-chip--ink' : 'lxr-chip--dashed'}`} data-state={state}>
            {state === 'final' ? '✓ Final' : '◌ Provisional'}
          </span>
        ) : undefined
      }
      banner={
        state === 'provisional' ? (
          <div className="lxr-banner" role="note">
            <strong>Provisional</strong>
            <span>Late data, open review items or trips still running can change these figures. They become final a few days after the day ends.</span>
          </div>
        ) : null
      }
      generatedAt={generatedAt}
      footer={
        <>
          <span>
            <ProvenanceKey /> · every figure from the LexiStar semantic layer
            {day.definitions_version ? ` (definitions ${String(day.definitions_version).slice(0, 12)})` : ''} · money is estimated
          </span>
          <span>{state === 'final' ? 'Final — reproducible for this closed day' : 'Provisional'}</span>
        </>
      }
    >
      {headline.length > 0 && (
        <dl className="lxr-kpis" aria-label="Headline figures">
          {headline.map((k) => {
            const v = values[k];
            const delta = deltaOf(v?.value, previous[k]?.value, unitOf(k, v), betterOf(k));
            return (
              <div className="lxr-kpi" key={k}>
                <dt>{nameOf(k)}</dt>
                <dd>
                  {formatValue(v?.value, unitOf(k, v))}
                  <Prov provenance={v?.provenance} />
                  <small>{changeWords(delta)} vs previous day</small>
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      <div className="lxr-grid lxr-grid--figures">
        <Section title="Top things to look at" aside={data.exceptions ? `${data.exceptions.length}` : undefined} wide>
          {data.exceptions === null ? (
            <p className="lxr-empty">Not available from the server yet.</p>
          ) : data.exceptions.length === 0 ? (
            <p className="lxr-empty">Nothing needs attention for this day.</p>
          ) : (
            <ol className="lxr-exceptions" aria-label="Top exceptions">
              {data.exceptions.slice(0, 5).map((x) => (
                <li key={`${x.kind}-${x.id}`}>
                  <div>
                    <span className="lxr-strong">
                      {!print && x.drill?.route ? <Link to={x.drill.route}>{x.title}</Link> : x.title}
                    </span>
                    {x.detail && <div className="lxr-muted">{x.detail}</div>}
                  </div>
                  <span className="lxr-tag lxr-tag--attention">{x.kind.replace(/_/g, ' ')}</span>
                </li>
              ))}
            </ol>
          )}
        </Section>

        {DAILY_SECTIONS.map((s) => {
          const figures = s.figures.filter((fig) => available.has(fig.key) || data.keys.includes(fig.key));
          if (!figures.length) return null;
          return (
            <Section key={s.id} title={s.title}>
              <table className="lxr-figures">
                <thead>
                  <tr>
                    <th>Figure</th>
                    <th>Day</th>
                    <th>Previous</th>
                    <th>Change</th>
                    <th>7 days</th>
                  </tr>
                </thead>
                <tbody>
                  {figures.map((fig) => {
                    const v = values[fig.key];
                    const p = previous[fig.key];
                    const unit = unitOf(fig.key, v);
                    const delta = deltaOf(v?.value, p?.value, unit, fig.better);
                    const route = drillOf(v);
                    const by = v?.by && Object.keys(v.by).length ? v.by : null;
                    const mix = v?.mix && Object.keys(v.mix).length ? v.mix : null;
                    return (
                      <tr key={fig.key} data-metric={fig.key}>
                        <td>
                          {!print && route ? <Link to={route}>{nameOf(fig.key)}</Link> : nameOf(fig.key)}
                          {by && (
                            <small style={{ display: 'block', fontSize: 10.5, color: 'var(--lxr-ink-3)' }}>
                              {Object.entries(by)
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 4)
                                .map(([k, n]) => `${k.replace(/_/g, ' ')} ${formatValue(n, unit)}`)
                                .join(' · ')}
                            </small>
                          )}
                          {mix && (
                            <small style={{ display: 'block', fontSize: 10.5, color: 'var(--lxr-ink-3)' }}>
                              {[
                                ['●', mix.measured],
                                ['◐', mix.derived],
                                ['○', mix.estimated],
                              ]
                                .filter(([, n]) => n)
                                .map(([g, n]) => `${String(g)} ${String(n)}`)
                                .join(' · ')}
                            </small>
                          )}
                        </td>
                        <td className="lxr-num lxr-value">
                          {formatValue(v?.value, unit)}
                          <Prov provenance={v?.provenance} />
                        </td>
                        <td className="lxr-num lxr-muted">{formatValue(p?.value, unit)}</td>
                        <td className="lxr-num">{changeWords(delta)}</td>
                        <td className="lxr-num">
                          <Spark values={trendOf(fig.key)} label={`${nameOf(fig.key)}, last seven days`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Section>
          );
        })}
      </div>
    </ReportDoc>
  );
}

export default function DailySummary() {
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const date = isDay(params.date) ? params.date : yesterdaySast();
  const print = usePrintMode();
  const rendering = useContext(ReportRenderContext);
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.reports.view');
  // The definition's optional scope (028: scope_type + scope_id on the query string).
  const scopeType = search.get('scope_type') || 'tenant';
  const scopeId = search.get('scope_id') || undefined;
  const [state, setState] = useState<{ load: LoadState; data: DailyData | null; error: string | null; at: string | null }>({
    load: 'loading',
    data: null,
    error: null,
    at: null,
  });

  const load = useCallback(async () => {
    if (!canView) return;
    setState((s) => ({ ...s, load: 'loading', error: null }));
    try {
      const data = await loadDaily(date, { scope_type: scopeType, scope_id: scopeType === 'tenant' ? undefined : scopeId });
      setState({ load: 'ready', data, error: null, at: new Date().toISOString() });
    } catch (err) {
      setState({ load: 'error', data: null, at: null, error: errorText(err, 'The daily summary could not be loaded.') });
    }
  }, [canView, date, scopeType, scopeId]);

  useEffect(() => {
    load();
  }, [load]);

  const goTo = (day: string) => navigate(`/lexistar/reports/daily/${day}${search.toString() ? `?${search.toString()}` : ''}`);
  const setPrint = (on: boolean) => {
    const next = new URLSearchParams(search);
    if (on) next.set('print', '1');
    else next.delete('print');
    setSearch(next, { replace: true });
  };
  const parameters = useMemo(
    () => (scopeType === 'tenant' ? { date } : { date, scope_type: scopeType, scope_id: scopeId }),
    [date, scopeType, scopeId]
  );

  if (!canView) {
    return (
      <ReportFrame print={print} state="error" error="forbidden">
        <PaperMessage print={print} title="You do not have permission to view reports." />
      </ReportFrame>
    );
  }

  const toolbar = rendering ? null : (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/lexistar/reports" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900 mr-2">
          <FaArrowLeft size={10} /> Reports
        </Link>
        {/* Phone: ◀ day ▶ on a row of its own; from sm the wrappers drop out (display: contents). */}
        <div className="flex w-full items-center gap-2 sm:contents">
          <button type="button" className={`${toolbarButton} touch-target`} onClick={() => goTo(addDays(date, -1))} aria-label="Previous day">
            <FaChevronLeft size={10} />
          </button>
          <div className="min-w-0 flex-1 sm:contents">
            <DayField value={date} onChange={goTo} />
          </div>
          <button
            type="button"
            className={`${toolbarButton} touch-target`}
            onClick={() => goTo(addDays(date, 1))}
            disabled={date >= todaySast()}
            aria-label="Next day"
          >
            <FaChevronRight size={10} />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {print ? (
          <>
            <button type="button" className={toolbarButton} onClick={() => setPrint(false)}>
              <FaDesktop size={11} /> Screen view
            </button>
            <button type="button" className={toolbarButton} onClick={() => window.print()}>
              <FaPrint size={11} /> Print
            </button>
          </>
        ) : (
          <button type="button" className={toolbarButton} onClick={() => setPrint(true)}>
            <FaPrint size={11} /> Print layout
          </button>
        )}
        <ExportButtons
          reportKey="daily_management_summary"
          parameters={parameters}
          formats={['pdf']}
          disabled={state.load !== 'ready'}
          filenameBase={`daily-summary-${date}`}
          buttonClassName={toolbarPrimary}
        />
      </div>
    </>
  );

  return (
    <ReportFrame print={print} state={state.load} error={state.error} toolbar={toolbar}>
      {state.load === 'loading' && !state.data ? (
        <PaperMessage print={print} title="Loading the daily summary…" />
      ) : state.error || !state.data ? (
        <PaperMessage print={print} title={state.error || 'The daily summary could not be loaded.'} />
      ) : (
        <DailyDocument
          date={date}
          data={state.data}
          print={print}
          generatedAt={state.at}
          scopeLabel={scopeType === 'tenant' ? null : `${scopeType.replace(/_/g, ' ')} ${state.data.day.scope?.label || scopeId || ''}`.trim()}
        />
      )}
    </ReportFrame>
  );
}
